/**
 * A value read now, then again every `intervalMs` while the tab is visible —
 * the header's badges.
 *
 * **Polling, not a socket.** There is no websocket or SSE in this application,
 * and adding one for a badge would drag in reconnection, a proxy rule in the
 * Vite dev server, and something that has to survive a `systemctl reload` — for
 * a number that may be a minute out of date.
 *
 * - **Stops while the tab is hidden**, and catches up the moment it is shown. A
 *   laptop left open overnight should not be a thousand requests nobody read.
 * - **A failed read is ignored.** A badge is not worth an error state; the next
 *   tick tries again, and anything really broken shows on a screen that matters.
 * - **Nothing is set after unmount.** A poll in flight at sign-out would
 *   otherwise land on a dead component — and its 401 would look like a session
 *   problem rather than a race.
 *
 * The setter is returned so the caller can apply a value it learned another
 * way — a mark-read answering with the new count — without waiting a minute.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function usePolledValue<T>(
  read: () => Promise<T>,
  initial: T,
  intervalMs = 60_000,
): [T, (value: T) => void] {
  const [value, setValue] = useState(initial);

  // Read through a ref, as useSubmit does: a caller's inline function is new
  // every render, and depending on it would restart the timer every render.
  const readRef = useRef(read);
  readRef.current = read;

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const set = useCallback((next: T) => {
    if (alive.current) setValue(next);
  }, []);

  useEffect(() => {
    const refresh = async () => {
      try {
        set(await readRef.current());
      } catch {
        // See above: the next tick tries again.
      }
    };
    void refresh();

    const tick = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const timer = window.setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [intervalMs, set]);

  return [value, set];
}
